import { useCallback, useEffect, useState } from 'react';
import { WbButton, WbModal } from '../wb';
import { salesforceClient } from '../../lib/salesforce';
import { syncEngine } from '../../lib/sync';
import { bevestigVertreklading, controleerLading, type VertrekItem } from '../../lib/route-start-controle';

export function RouteStartControle({ routeId, stopIds, onReady, onCancel }: {
  routeId: string;
  stopIds: string[];
  onReady: () => void;
  onCancel: () => void;
}) {
  const [items, setItems] = useState<VertrekItem[]>([]);
  const [bevestigd, setBevestigd] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const stopSleutel = JSON.stringify([...stopIds].sort());

  const controleer = useCallback(async (opslaan = false) => {
    setBusy(true);
    setError(null);
    try {
      if (syncEngine.hasPendingSyncs()) await syncEngine.forceSync();
      if (syncEngine.hasPendingSyncs()) throw new Error('Er staan nog wijzigingen klaar om te versturen. Maak verbinding en probeer opnieuw.');
      const ids = new Set<string>(JSON.parse(stopSleutel));
      const ontbrekend = opslaan
        ? await bevestigVertreklading(salesforceClient, routeId, ids, [...bevestigd])
        : await controleerLading(salesforceClient, routeId, ids);
      setItems(ontbrekend);
      setBevestigd(new Set());
      if (!ontbrekend.length) onReady();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'De laadcontrole is mislukt.');
    } finally {
      setBusy(false);
    }
  }, [routeId, stopSleutel, bevestigd, onReady]);

  // Een nieuwe stoplijst betekent opnieuw meten; vinkjes starten altijd leeg.
  useEffect(() => { void controleer(); }, [routeId, stopSleutel]); // eslint-disable-line react-hooks/exhaustive-deps

  return <WbModal open onClose={onCancel} sluitbaar={!busy} sluitOpAchtergrond={false}
    titel="Controleer de lading"
    acties={<>
      <WbButton variant="secondary" disabled={busy} onClick={onCancel}>Terug naar routes</WbButton>
      <WbButton disabled={busy || (!error && (!items.length || bevestigd.size !== items.length))}
        onClick={() => void controleer(!error)}>
        {busy ? 'Lading controleren…' : error ? 'Opnieuw controleren' : 'Bevestigen en route starten'}
      </WbButton>
    </>}>
    {busy && <p role="status">De actuele laadstatus wordt opgehaald.</p>}
    {error && <p role="alert" className="text-wb-geldink font-bold">{error}</p>}
    {!busy && !error && <>
      <p>Deze items staan nog niet op ‘In bus’. Controleer of ze echt in de bus liggen en vink ze één voor één aan.</p>
      <div className="mt-4 space-y-2">
        {items.map(item => <label key={item.itemId} className="flex gap-3 items-start min-h-tap border-wb border-wb-rand rounded-wb p-3">
          <input type="checkbox" className="mt-1 h-6 w-6 shrink-0" checked={bevestigd.has(item.itemId)}
            onChange={event => setBevestigd(prev => {
              const next = new Set(prev);
              if (event.target.checked) next.add(item.itemId); else next.delete(item.itemId);
              return next;
            })} />
          <span><strong>{item.itemName} · {item.customerName}</strong><br />
            {item.productName}{item.dimensions ? ` · ${item.dimensions}` : ''}<br />
            <span className="text-wb-inkt2">Status: {item.status || 'onbekend'} · Ligt in de bus</span>
          </span>
        </label>)}
      </div>
      <p className="mt-3 text-sm text-wb-inkt2">Mist er iets? Ga terug en laad het eerst in.</p>
    </>}
  </WbModal>;
}
